import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { DownloadSimpleIcon, XIcon } from "@phosphor-icons/react";

/**
 * Descarga del PDF de una factura por el RTC: antes de descargar pide una nota o comentario y la registra en la
 * Bitácora de descargas de la factura. El servidor entrega el PDF solo con el «pase» que devuelve ese registro.
 * Los demás perfiles descargan como siempre.
 *
 *   const pase = await pedirPaseFactura(factura);   // null si el usuario cancela; "" si no es RTC
 *   const url = conPase(urlPdf, pase);
 */
let abrir = null;           // lo registra <NotaDescargaHost />
let esRtcActual = false;

export const esRtcDescarga = () => esRtcActual;

export function pedirPaseFactura(factura) {
  if (!esRtcActual) return Promise.resolve("");
  if (!abrir) return Promise.resolve(null);
  return new Promise((resolve) => abrir(factura, resolve));
}

export const conPase = (url, pase) => (pase ? `${url}${url.includes("?") ? "&" : "?"}pase=${encodeURIComponent(pase)}` : url);

/** Descarga un archivo autenticado como archivo (no ventana emergente: no lo bloquea el navegador). */
export async function descargarArchivo(path, nombre) {
  const { data } = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = nombre; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export default function NotaDescargaHost() {
  const { user } = useAuth();
  esRtcActual = Boolean(user?.effective?.alcance_regional);
  const [pedido, setPedido] = useState(null);       // {factura, resolve}
  const [nota, setNota] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);
  const area = useRef(null);

  useEffect(() => {
    abrir = (factura, resolve) => { setNota(""); setError(""); setPedido({ factura, resolve }); };
    return () => { abrir = null; };
  }, []);
  useEffect(() => { if (pedido) setTimeout(() => area.current?.focus(), 50); }, [pedido]);

  if (!pedido) return null;
  const cerrar = (valor) => { pedido.resolve(valor); setPedido(null); };
  const enviar = async (e) => {
    e.preventDefault();
    if (enviando) return;
    if (nota.trim().length < 3) { setError("Escribe la nota o comentario de la descarga."); return; }
    setEnviando(true); setError("");
    try {
      const { data } = await api.post(`/invoices/${pedido.factura.id}/descargas`, { nota: nota.trim() });
      cerrar(data.pase);
    } catch (er) { setError(apiErr(er)); } finally { setEnviando(false); }
  };
  const f = pedido.factura;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" role="dialog" aria-modal="true" aria-labelledby="nota-descarga-titulo"
      onKeyDown={(e) => e.key === "Escape" && cerrar(null)}>
      <form onSubmit={enviar} className="bg-card border border-border rounded-xl w-full max-w-md" data-testid="nota-descarga">
        <div className="p-4 border-b border-border flex items-start justify-between gap-3">
          <div>
            <h3 id="nota-descarga-titulo" className="font-heading text-base font-semibold flex items-center gap-2"><DownloadSimpleIcon size={18} /> Descargar factura en PDF</h3>
            <p className="text-xs text-text-secondary mt-0.5"><span className="font-mono">{f.numero}</span>{f.cliente_nombre ? ` · ${f.cliente_nombre}` : ""}</p>
          </div>
          <button type="button" onClick={() => cerrar(null)} aria-label="Cancelar" className="p-2 text-text-muted hover:text-text-primary"><XIcon size={18} /></button>
        </div>
        <div className="p-4 space-y-2">
          <label htmlFor="nota-descarga" className="block text-xs font-semibold text-text-secondary">Nota o comentario de la descarga <span className="text-red-300">*</span></label>
          <textarea id="nota-descarga" ref={area} rows={3} maxLength={500} value={nota} onChange={(e) => setNota(e.target.value)}
            aria-invalid={Boolean(error)} placeholder="Ej.: Factura descargada para enviar al cliente."
            className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="nota-descarga-texto" />
          <p className="text-[11px] text-text-muted">Queda en la bitácora de la factura con tu usuario, la fecha y la hora.</p>
          {error && <p className="text-xs text-red-300" role="alert">{error}</p>}
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button type="button" onClick={() => cerrar(null)} className="min-h-[44px] px-4 border border-border rounded-md text-sm">Cancelar</button>
          <button type="submit" disabled={enviando} data-testid="nota-descarga-confirmar"
            className="min-h-[44px] px-4 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md text-sm flex items-center gap-2 disabled:opacity-60">
            <DownloadSimpleIcon size={16} weight="bold" /> {enviando ? "Registrando…" : "Registrar y descargar"}</button>
        </div>
      </form>
    </div>
  );
}
